// Comprehensive Zone-to-Zone Fare Matrix
// LPIA fares are official government rates
// Other routes are estimated based on distance and standard taxi rates
//
// 2026-06-25 — Owner-approved $5 base-fare reduction across the entire matrix.
// LPIA totals dropped by $5; non-LPIA inter-zone customer totals also dropped
// by $5 because the silent +$5 fuel adjustment is preserved (matrix -$5, then
// +$5 fuel = net -$5 for the customer). Tolls, shopping stops, and Sun Med
// $125 fixed contract are NOT affected.
//
// 2026-07-11 — Owner-approved LPIA rate update. Only the 16 LPIA-to-destination
// fares below were adjusted (preview only). Inter-zone matrix, fuel rule,
// $2 Paradise Island bridge toll, waiting-time rules, shopping-stop prices,
// add-on fees, Sun Med $125 contract, tours (not present), and PayPal path
// are all UNCHANGED.

export const zones = [
  { id: 'lpia', name: 'LPIA Airport', isAirport: true },
  { id: 'bain-town', name: 'Bain Town & Grants Town', fare: 40.00 },
  { id: 'cable-beach', name: 'Cable Beach', fare: 35.00 },
  { id: 'coral-harbour', name: 'Coral Harbour', fare: 35.00 },
  { id: 'downtown', name: 'Downtown', fare: 45.00 },
  { id: 'elizabeth-east-end', name: 'Elizabeth & East End', fare: 65.00 },
  { id: 'englerston', name: 'Englerston', fare: 35.00 },
  { id: 'fox-hill', name: 'Fox Hill', fare: 50.00 },
  { id: 'lyford-cay', name: 'Lyford Cay', fare: 45.00 },
  { id: 'mount-pleasant', name: 'Mount Pleasant', fare: 40.00 },
  { id: 'marathon', name: 'Marathon', fare: 37.00 },
  { id: 'montague-shores', name: 'Montague Shores', fare: 55.00 },
  { id: 'paradise-island', name: 'Paradise Island', fare: 53.00, bridgeToll: 2.00 },
  { id: 'prince-george-dock', name: 'Prince George Dock', fare: 50.00 },
  { id: 'south-ocean', name: 'South Ocean', fare: 40.00 },
  { id: 'winton', name: 'Winton / Winton Estates', fare: 65.00 },
  { id: 'yamacraw', name: 'Yamacraw / Eastern Shores', fare: 65.00 }
];

// Zone-to-Zone fare matrix (any location to any location)
// Format: { from-to: fare, to-from: fare }
// All values reduced by $5 vs the prior matrix (2026-06-25, owner-approved).
export const zoneToZoneFares = {
  // LPIA to all destinations (fuel cost baked in; no silent +$5 added).
  // 2026-07-11 owner-approved LPIA rate refresh.
  'lpia-bain-town': 40.00,
  'lpia-cable-beach': 35.00,
  'lpia-coral-harbour': 35.00,
  'lpia-downtown': 45.00,
  'lpia-elizabeth-east-end': 65.00,
  'lpia-englerston': 35.00,
  'lpia-lyford-cay': 45.00,
  'lpia-mount-pleasant': 40.00,
  'lpia-marathon': 37.00,
  'lpia-montague-shores': 55.00,
  'lpia-paradise-island': 53.00,
  'lpia-prince-george-dock': 50.00,
  'lpia-south-ocean': 40.00,
  'lpia-winton': 65.00,
  'lpia-fox-hill': 50.00,
  'lpia-yamacraw': 65.00,

  // Inter-zone entries (non-LPIA). Stored values are BASE fares; the silent
  // +$5 fuel adjustment is automatically added inside calculateFare for any
  // non-LPIA pair, so customer totals appear $5 higher than these numbers
  // (plus $2 toll where Paradise Island is involved).

  // Winton / Winton Estates
  'winton-bain-town': 12.00,
  'winton-cable-beach': 18.00,
  'winton-coral-harbour': 22.00,
  'winton-downtown': 12.00,
  'winton-elizabeth-east-end': 5.00,
  'winton-englerston': 7.00,
  'winton-lyford-cay': 35.00,
  'winton-mount-pleasant': 15.00,
  'winton-marathon': 5.00,
  'winton-montague-shores': 5.00,
  'winton-paradise-island': 15.00,
  'winton-prince-george-dock': 12.00,
  'winton-south-ocean': 28.00,
  'winton-fox-hill': 5.00,
  'winton-yamacraw': 5.00,
  // Fox Hill
  'fox-hill-bain-town': 13.00,
  'fox-hill-cable-beach': 18.00,
  'fox-hill-coral-harbour': 20.00,
  'fox-hill-downtown': 13.00,
  'fox-hill-elizabeth-east-end': 7.00,
  'fox-hill-englerston': 10.00,
  'fox-hill-lyford-cay': 35.00,
  'fox-hill-mount-pleasant': 17.00,
  'fox-hill-marathon': 7.00,
  'fox-hill-montague-shores': 7.00,
  'fox-hill-paradise-island': 15.00,
  'fox-hill-prince-george-dock': 13.00,
  'fox-hill-south-ocean': 28.00,
  'fox-hill-yamacraw': 7.00,
  // Yamacraw / Eastern Shores
  'yamacraw-bain-town': 18.00,
  'yamacraw-cable-beach': 25.00,
  'yamacraw-coral-harbour': 25.00,
  'yamacraw-downtown': 20.00,
  'yamacraw-elizabeth-east-end': 2.00,
  'yamacraw-englerston': 15.00,
  'yamacraw-lyford-cay': 43.00,
  'yamacraw-mount-pleasant': 25.00,
  'yamacraw-marathon': 10.00,
  'yamacraw-montague-shores': 7.00,
  'yamacraw-paradise-island': 20.00,
  'yamacraw-prince-george-dock': 25.00,
  'yamacraw-south-ocean': 33.00,

  // Downtown to other zones
  'downtown-cable-beach': 17.00,
  'downtown-paradise-island': 15.00,
  'downtown-prince-george-dock': 10.00,
  'downtown-bain-town': 7.00,
  'downtown-englerston': 10.00,
  'downtown-marathon': 13.00,
  'downtown-montague-shores': 15.00,
  'downtown-lyford-cay': 30.00,
  'downtown-coral-harbour': 20.00,
  'downtown-mount-pleasant': 15.00,
  'downtown-south-ocean': 23.00,
  'downtown-elizabeth-east-end': 17.00,

  // Cable Beach to other zones
  'cable-beach-paradise-island': 20.00,
  'cable-beach-lyford-cay': 13.00,
  'cable-beach-bain-town': 15.00,
  'cable-beach-englerston': 13.00,
  'cable-beach-downtown': 17.00,
  'cable-beach-prince-george-dock': 23.00,
  'cable-beach-marathon': 20.00,
  'cable-beach-coral-harbour': 30.00,
  'cable-beach-mount-pleasant': 23.00,
  'cable-beach-south-ocean': 33.00,
  'cable-beach-montague-shores': 25.00,
  'cable-beach-elizabeth-east-end': 27.00,

  // Paradise Island to other zones
  'paradise-island-prince-george-dock': 13.00,
  'paradise-island-bain-town': 17.00,
  'paradise-island-englerston': 20.00,
  'paradise-island-marathon': 17.00,
  'paradise-island-montague-shores': 15.00,
  'paradise-island-lyford-cay': 35.00,
  'paradise-island-coral-harbour': 33.00,
  'paradise-island-mount-pleasant': 30.00,
  'paradise-island-south-ocean': 30.00,
  'paradise-island-elizabeth-east-end': 20.00,

  // Other inter-zone fares
  'bain-town-englerston': 7.00,
  'bain-town-marathon': 10.00,
  'bain-town-prince-george-dock': 13.00,
  'bain-town-montague-shores': 15.00,
  'bain-town-coral-harbour': 23.00,
  'bain-town-mount-pleasant': 17.00,
  'bain-town-lyford-cay': 30.00,
  'bain-town-south-ocean': 27.00,
  'bain-town-elizabeth-east-end': 13.00,

  'englerston-marathon': 7.00,
  'englerston-montague-shores': 13.00,
  'englerston-coral-harbour': 20.00,
  'englerston-mount-pleasant': 15.00,
  'englerston-lyford-cay': 27.00,
  'englerston-south-ocean': 25.00,
  'englerston-prince-george-dock': 15.00,
  'englerston-elizabeth-east-end': 10.00,

  'marathon-montague-shores': 10.00,
  'marathon-coral-harbour': 17.00,
  'marathon-mount-pleasant': 13.00,
  'marathon-lyford-cay': 30.00,
  'marathon-south-ocean': 23.00,
  'marathon-prince-george-dock': 17.00,
  'marathon-elizabeth-east-end': 7.00,

  'montague-shores-elizabeth-east-end': 7.00,
  'montague-shores-coral-harbour': 23.00,
  'montague-shores-mount-pleasant': 20.00,
  'montague-shores-lyford-cay': 37.00,
  'montague-shores-south-ocean': 30.00,
  'montague-shores-prince-george-dock': 20.00,

  'coral-harbour-mount-pleasant': 10.00,
  'coral-harbour-lyford-cay': 33.00,
  'coral-harbour-south-ocean': 15.00,
  'coral-harbour-prince-george-dock': 30.00,
  'coral-harbour-elizabeth-east-end': 23.00,

  'mount-pleasant-lyford-cay': 30.00,
  'mount-pleasant-south-ocean': 17.00,
  'mount-pleasant-prince-george-dock': 25.00,
  'mount-pleasant-elizabeth-east-end': 20.00,

  'lyford-cay-south-ocean': 35.00,
  'lyford-cay-prince-george-dock': 37.00,
  'lyford-cay-elizabeth-east-end': 40.00,

  'south-ocean-prince-george-dock': 33.00,
  'south-ocean-elizabeth-east-end': 30.00,

  'prince-george-dock-elizabeth-east-end': 23.00
};

// Official add-on charges per Road Traffic regulations
// 2026-06-25 — Owner-approved updates: luggage $4→$5, oversized $10→$7,
// car seat $15→$10. Waiting time: first 15 minutes free, then $1.50/min.
// 2026-07-11 — Owner-approved update: no free waiting time. Waiting is
// charged from minute 1 at $1.50/min ($90/hour). Shopping-stop fixed prices
// removed — those stops are now billed via waiting time.
export const additionalCharges = {
  extraPassenger: 5.00,           // Per passenger beyond 2
  extraLuggage: 5.00,             // Per additional regular suitcase beyond 2 included (updated 2026-06-25)
  largeBag: 7.00,                 // Per oversized/heavy item / golf bag / box (updated 2026-06-25)
  childrenUnder3: 0,              // Free
  carSeat: 10.00,                 // Per car seat (updated 2026-06-25 — flat additional charge)
  waitingTimePerMinute: 1.50,     // Charged from minute 1 ($90/hour, owner-approved 2026-07-11)
  waitingTimeFreeMinutes: 0,      // 2026-07-11 — no free minutes; every minute is billed
  paradiseIslandToll: 2.00        // Bridge toll (always shown separately)
};

// Shopping Plaza Stop — customer selects the store type; the stop itself
// carries NO fixed fee. Actual charge comes from waiting time entered by
// the customer / driver ($1.50/min from minute 1). Radio-button selection
// in the UI ensures only ONE option is ever active at a time.
// 2026-07-11 — Owner-approved: fixed shopping-stop prices removed.
export const SHOPPING_STOP_OPTIONS = {
  none:    { id: 'none',    label: 'No Shopping Plaza Stop',                                     price: 0, display: 'None',                          minutes: 0 },
  grocery: { id: 'grocery', label: 'Grocery Store Stop — charged by waiting time',              price: 0, display: 'Grocery Store Stop',            minutes: 0 },
  liquor:  { id: 'liquor',  label: 'Liquor Store Stop — charged by waiting time',               price: 0, display: 'Liquor Store Stop',             minutes: 0 },
  both:    { id: 'both',    label: 'Grocery + Liquor Store Stop — charged by waiting time',    price: 0, display: 'Grocery + Liquor Store Stop',   minutes: 0 },
};

export const workSchedule = {
  regularDays: {
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    startTime: '05:00',
    endTime: '23:00'
  },
  sunday: {
    day: 'Sunday',
    note: 'Pre-arranged outings only'
  }
};

export const calculateFare = (
  pickupZone,
  dropoffZone,
  passengers = 2,
  luggagePieces = 2,
  largeBags = 0,
  childrenUnder3 = 0,
  carSeats = 0,
  waitingMinutes = 0,
  additionalStops = [],
  shoppingStop = 'none'
) => {
  let baseFare = 0;
  let breakdown = {
    baseFare: 0,
    extraPassengers: 0,
    extraLuggage: 0,
    largeBags: 0,
    bridgeToll: 0,
    carSeats: 0,
    waitingTime: 0,
    additionalStops: 0,
    shoppingStop: 0,
    shoppingStopOption: 'none',
    total: 0
  };

  // Get base fare from zone-to-zone matrix
  const fareKey1 = `${pickupZone}-${dropoffZone}`;
  const fareKey2 = `${dropoffZone}-${pickupZone}`; // Try reverse

  if (zoneToZoneFares[fareKey1]) {
    baseFare = zoneToZoneFares[fareKey1];
  } else if (zoneToZoneFares[fareKey2]) {
    baseFare = zoneToZoneFares[fareKey2];
  } else {
    // Defensive fallback — should NEVER trigger from the public UI because every
    // distinct-zone pair in the `zones` array has a matrix entry. The only way
    // to reach this branch is if (a) someone selects the same zone for pickup &
    // drop-off, or (b) a new zone is added to `zones` without its matrix rows.
    //
    // 2026-06-25 (LB-2): kept at the pre-update value of $30 (NOT reduced by
    // $5) per the owner-approved rule that the fallback must never create a
    // lower-than-intended fare by accident. The visible $5 reduction applies
    // only to genuine published taxi rates in the matrix.
    baseFare = 30.00;
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        `[Taxi-98 fareData] ⚠️  Fallback fare $${baseFare.toFixed(2)} applied for ` +
        `unknown route "${pickupZone}" → "${dropoffZone}". Add this pair to ` +
        `zoneToZoneFares in fareData.js, or block same-zone selections in the UI.`
      );
    }
  }

  // Silent fuel adjustment applies ONLY to inter-zone routes.
  // LPIA fares (from or to) include fuel directly in the matrix value.
  if (baseFare > 0 && pickupZone !== 'lpia' && dropoffZone !== 'lpia') {
    baseFare += 5.00;
  }

  breakdown.baseFare = baseFare;

  // Add Paradise Island bridge toll if either zone is Paradise Island
  if (pickupZone === 'paradise-island' || dropoffZone === 'paradise-island') {
    breakdown.bridgeToll = additionalCharges.paradiseIslandToll;
  }

  // Calculate passenger charges (children under 3 ride free)
  const totalPassengers = passengers - childrenUnder3;
  if (totalPassengers > 2) {
    breakdown.extraPassengers = (totalPassengers - 2) * additionalCharges.extraPassenger;
  }

  // Calculate luggage charges (first 2 bags free)
  if (luggagePieces > 2) {
    breakdown.extraLuggage = (luggagePieces - 2) * additionalCharges.extraLuggage;
  }

  // Large bags/boxes
  if (largeBags > 0) {
    breakdown.largeBags = largeBags * additionalCharges.largeBag;
  }

  // Car seats
  if (carSeats > 0) {
    breakdown.carSeats = carSeats * additionalCharges.carSeat;
  }

  // Waiting time — charged from minute 1 at $1.50/min ($90/hour).
  // Owner-approved 2026-07-11: no free minutes.
  const freeMinutes = additionalCharges.waitingTimeFreeMinutes || 0;
  if (waitingMinutes > freeMinutes) {
    breakdown.waitingTime = (waitingMinutes - freeMinutes) * additionalCharges.waitingTimePerMinute;
  }

  // Additional stops ($10 per stop)
  if (additionalStops && additionalStops.length > 0) {
    const validStops = additionalStops.filter(stop => stop && stop.trim() !== '');
    breakdown.additionalStops = validStops.length * 10;
  }

  // Shopping Plaza Stop — single-select, mutually exclusive options
  const shoppingOpt = SHOPPING_STOP_OPTIONS[shoppingStop] || SHOPPING_STOP_OPTIONS.none;
  breakdown.shoppingStop = shoppingOpt.price;
  breakdown.shoppingStopOption = shoppingOpt.id;

  // Calculate total
  breakdown.total =
    breakdown.baseFare +
    breakdown.extraPassengers +
    breakdown.extraLuggage +
    breakdown.largeBags +
    breakdown.bridgeToll +
    breakdown.carSeats +
    breakdown.waitingTime +
    (breakdown.additionalStops || 0) +
    (breakdown.shoppingStop || 0);

  return breakdown;
};

export const contactInfo = {
  phone: '+1 (242) 810-7335',
  phone2: '+1 (242) 810-0098',
  whatsapp: '+12428107335',
  whatsapp2: '+12428100098',
  email: 'bookings@taxi-98.com',
  emailStephen: 'stephen@taxi-98.com',
  address: 'Nassau, New Providence',
  city: 'Bahamas',
  hours: 'Mon-Sat: 5:00 AM - 11:00 PM, Sun: Pre-arranged only',
  social: {
    facebook: 'https://www.facebook.com/profile.php?id=61574314732077',
    instagram: 'https://www.instagram.com/taxi98bahamas',
    twitter: 'https://x.com/Taxi98Bahamas'
  }
};
